export interface Article {
  id: number;
  title: string;
  slug: string;
  excerpt: string;
  content: string;
  category: string;
  categorySlug: string;
  author: string;
  date: string;
  imageUrl: string;
  tags: string[];
  readTime: string;
  featured: boolean;
}

export const categories = [
  { name: '全部文章', slug: 'all', icon: 'ri-book-open-line', description: '浏览所有事奉装备资源' },
  { name: '讲道资源', slug: 'sermon-resources', icon: 'ri-megaphone-line', description: '系统解经、主题讲道、节期讲章' },
  { name: '门徒训练', slug: 'discipleship-training', icon: 'ri-user-follow-line', description: '一对一陪谈、小组带领、门训课程' },
  { name: '灵命成长', slug: 'spiritual-growth', icon: 'ri-heart-pulse-line', description: '灵修操练、祷告生活、品格塑造' },
  { name: '牧养实践', slug: 'pastoral-practice', icon: 'ri-hand-heart-line', description: '探访关怀、教会治理、危机辅导' },
];

export const articles: Article[] = [
  {
    id: 1,
    title: '以基督为中心的讲道：从经文中看见救赎的脉络',
    slug: 'christ-centered-preaching',
    excerpt: '真正合乎圣经的讲道，不是将道德教训叠加在经文之上，而是在每一段经文中发现指向基督的救赎轨迹。本文探讨如何预备一篇以基督为中心的讲章。',
    content: `<h2>引言：讲道的核心是什么？</h2>
<p>每一位站在讲台上的牧者都曾问过自己这个问题。我们是在传递一套道德规范吗？是在分享一些人生智慧吗？还是在对会众进行心理辅导？</p>
<p>使徒保罗在哥林多前书清楚地宣告：「因为我曾定了主意，在你们中间不知道别的，只知道耶稣基督并他钉十字架。」（林前2:2）这应当成为每一次讲道的根基。</p>

<h2>一、重新理解「以基督为中心」</h2>
<p>以基督为中心的讲道，并非意味着每一篇讲章都要机械地跳到各各他的十字架。而是要在所宣讲的经文中，发现那一位救赎历史的主角——耶稣基督——如何在这段经文中被预表、被应许、被彰显。</p>
<p>路加福音记载，复活的主亲自向以马忤斯路上的两个门徒讲解圣经：「于是从摩西和众先知起，凡经上所指着自己的话都给他们讲解明白了。」（路24:27）</p>

<h2>二、从经文中发现基督的轨迹</h2>
<p>预备讲章时，我们可以问以下几个问题：</p>
<ul>
<li>这段经文在救赎历史中处于什么位置？</li>
<li>它如何指向或预备基督的到来？</li>
<li>基督如何成全了这段经文所指向的？</li>
<li>因着基督的成全，今天的信徒当如何回应？</li>
</ul>

<h2>三、实际操练：以路得记为例</h2>
<p>路得记是一个美丽的故事，但若只停留在「婆媳关系」或「忠诚美德」的层面，就错失了它真正的力量。波阿斯作为至近的亲属（go'el），预表了那位将来要救赎我们脱离罪恶捆绑的基督。路得的蒙恩被接纳，指向了外邦人在基督里被收纳为神子民的奇妙恩典。</p>

<blockquote>「讲道者的任务不是发明新的真理，而是在古老的经文中，帮助神的百姓看见那一位永恒的新郎——耶稣基督。」</blockquote>

<h2>结语</h2>
<p>愿我们每一次站上讲台，都能像施洗约翰一样指着耶稣说：「他必兴旺，我必衰微。」（约3:30）因为真正喂养群羊的，从来不是讲员的口才，而是基督自己在经文中的显现。</p>`,
    category: '讲道资源',
    categorySlug: 'sermon-resources',
    author: '王牧',
    date: '2026-07-15',
    imageUrl: 'https://readdy.ai/api/search-image?query=Warm%20wooden%20church%20pulpit%20with%20open%20Bible%20and%20soft%20natural%20light%20streaming%20through%20stained%20glass%20window%2C%20reverent%20atmosphere%2C%20warm%20golden%20tones%2C%20peaceful%20and%20sacred%20space%2C%20minimalist%20still%20life%20photography%20with%20shallow%20depth%20of%20field&width=800&height=450&seq=article-sermon-01&orientation=landscape',
    tags: ['讲道学', '释经讲道', '基督中心'],
    readTime: '12分钟',
    featured: true,
  },
  {
    id: 2,
    title: '门徒训练的五个关键阶段：从初信到成熟',
    slug: 'five-stages-discipleship',
    excerpt: '门徒训练不是一次性的课程，而是一个生命影响生命的过程。本文将门训分为五个阶段，帮助牧者有计划地陪伴信徒从灵命初生走向成熟。',
    content: `<h2>引言：门训是一场马拉松</h2>
<p>在大使命中，主耶稣吩咐我们「使万民作我的门徒」，并且要「凡我所吩咐你们的，都教训他们遵守」（太28:19-20）。这不是一个速成的过程，而是需要耐心、陪伴和榜样的漫长旅程。</p>

<h2>第一阶段：建立关系（1-3个月）</h2>
<p>门训从关系开始。在这个阶段，重点是建立信任和安全感。不要急于灌输知识，而是通过一起吃饭、散步、祷告，让被门训者感受到真诚的关怀。耶稣呼召门徒的第一件事，就是「来跟从我」——是关系的建立，而不是课程的报名。</p>

<h2>第二阶段：根基建造（3-6个月）</h2>
<p>当关系建立之后，开始系统地帮助对方建立信仰的根基：确据的建立、读经的习惯、祷告的生活、教会的归属。使用一本好的门训材料，但不要让材料取代了关系的互动。</p>

<h2>第三阶段：生命对付（6-12个月）</h2>
<p>根基建立后，圣灵会开始在生命中动工，显明那些需要被对付的老我。这个阶段可能会比较痛苦，但也是成长最快的时期。门训者需要以恩典和真理并行，帮助对方诚实地面对自己。</p>

<h2>第四阶段：恩赐发现与服侍（12-18个月）</h2>
<p>当生命被对付到一定程度，就可以帮助对方发现属灵恩赐，并创造机会让其在安全的范围内操练服侍。从简单的任务开始，逐步增加责任。</p>

<h2>第五阶段：差派与倍增（18个月以后）</h2>
<p>门训的终极目标是使他能够去造就别人。当对方已经成熟到可以独立服侍甚至带领他人时，就是差派的时候了。这正是提摩太后书2:2的应许模式。</p>

<blockquote>「最好的门训不是制造依赖你的跟随者，而是培养能够独立跟随基督并带领他人的门徒。」</blockquote>`,
    category: '门徒训练',
    categorySlug: 'discipleship-training',
    author: '李传道',
    date: '2026-07-10',
    imageUrl: 'https://readdy.ai/api/search-image?query=Two%20people%20sitting%20on%20a%20wooden%20bench%20in%20a%20peaceful%20garden%20having%20a%20deep%20conversation%2C%20warm%20sunlight%20filtering%20through%20trees%2C%20mentoring%20relationship%2C%20calm%20and%20nurturing%20atmosphere%2C%20soft%20natural%20colors&width=800&height=450&seq=article-disciple-01&orientation=landscape',
    tags: ['门徒训练', '灵命成长', '小组牧养'],
    readTime: '10分钟',
    featured: true,
  },
  {
    id: 3,
    title: '旷野中的灵修：如何在干旱季节维持属灵生命',
    slug: 'wilderness-devotion',
    excerpt: '每个基督徒都会经历属灵的「旷野期」。在这些干旱的季节里，我们当如何保持与神的亲密关系？本文从圣经与实践角度提供切实可行的建议。',
    content: `<h2>引言：旷野是必经之路</h2>
<p>以色列人在进入应许之地前，必须先经过旷野。主耶稣在开始公开事工之前，也被圣灵引到旷野。旷野并非神离弃我们的记号，恰恰相反，它往往是神更深塑造我们的场所。</p>
<p>然而，旷野的季节确实艰难。当读经变得枯燥、祷告如同对空气说话、敬拜失去热情时，我们当如何自处？</p>

<h2>一、承认旷野的正当性</h2>
<p>第一步是停止定罪自己。许多信徒在灵命低潮时会想：「一定是我的罪导致了这种情况」或「我的信心出了严重问题」。虽然罪确实会影响我们与神的关系，但并非每一个旷野季节都是罪的结果。</p>
<p>有时旷野恰恰是神主动的安排。何西阿书说：「我必劝导她，领她到旷野，对她说安慰的话。」（何2:14）神有时将我们带入旷野，是为了单独对我们说话。</p>

<h2>二、在干旱中建立规则</h2>
<p>当感觉消失时，正是操练纪律的时候。感觉是不可靠的，但习惯可以承载我们度过最干旱的季节。即使「没有感觉」，仍然坚持固定的灵修时间、参加聚会、背诵经文。</p>

<h2>三、寻找旷野中的吗哪</h2>
<p>神在旷野中天天赐下吗哪给以色列人，但吗哪需要每天清晨去拾取。旷野中的恩典往往是日用而不储存的。学习在每一天中寻找神微小的恩典痕迹——一个感动的念头、一段恰好出现的经文、一次及时的问候。</p>

<h2>四、不要独自走过旷野</h2>
<p>以利亚在旷野中极度低落时，神差派天使来供应他，也为他预备了以利沙。寻找属灵同伴，坦诚分享你的挣扎，让团契成为旷野中的绿洲。</p>

<blockquote>「旷野不是终点，而是一条通道。那位曾经走过旷野的主，如今正陪着你走过你的旷野。」</blockquote>`,
    category: '灵命成长',
    categorySlug: 'spiritual-growth',
    author: '陈弟兄',
    date: '2026-07-05',
    imageUrl: 'https://readdy.ai/api/search-image?query=A%20single%20person%20silhouette%20sitting%20on%20a%20rock%20overlooking%20a%20vast%20desert%20landscape%20at%20sunrise%2C%20warm%20golden%20light%20breaking%20through%20clouds%2C%20contemplative%20and%20hopeful%20mood%2C%20minimalist%20composition%2C%20soft%20earthy%20tones&width=800&height=450&seq=article-spiritual-01&orientation=landscape',
    tags: ['灵修', '属灵争战', '祷告生活'],
    readTime: '8分钟',
    featured: true,
  },
  {
    id: 4,
    title: '冲突中的牧养智慧：教会内部矛盾的化解之道',
    slug: 'pastoral-conflict-resolution',
    excerpt: '教会中难免有冲突。作为牧者，如何在矛盾中既持守真理又彰显爱心？本文从圣经原则出发，提供处理教会内部纠纷的实战指南。',
    content: `<h2>引言：冲突是不可避免的</h2>
<p>只要有两个罪人在一起，就有可能产生冲突。使徒行传记载的第一次教会冲突，发生在使徒时代最复兴的耶路撒冷教会中——希腊化的犹太人和希伯来人之间因为饭食分配问题产生争执（徒6:1）。</p>
<p>冲突的存在并不代表教会不属灵。关键在于如何面对和处理。</p>

<h2>一、不要回避，但要冷静</h2>
<p>许多牧者对冲突的第一反应是逃避。但逃避只会让问题发酵。保罗在以弗所书4:26告诉我们：「生气却不要犯罪，不可含怒到日落。」处理冲突有一个时间窗口——不要拖延，但也不要冲动。</p>

<h2>二、先聆听，后发言</h2>
<p>雅各书1:19说：「你们各人要快快地听，慢慢地说，慢慢地动怒。」在牧养冲突中，80%的时间应该用于聆听。让每一方都觉得自己的声音被听到了。很多时候，当人觉得被真正理解时，问题的严重性已经减轻了一半。</p>

<h2>三、区分「原则性问题」和「偏好性问题」</h2>
<p>不是所有的分歧都关乎真理。音乐风格、聚会时间、装修颜色——这些都是偏好问题。在非原则性问题上，教导会众操练彼此相爱和舍己。在原则性问题上，则需要以温柔但坚定的态度持守真理。</p>

<h2>四、寻求和解而非胜利</h2>
<p>教会冲突的目标不是「谁赢了」，而是「如何荣耀神并使关系得以修复」。马太福音18章的和好程序——先私下、再带见证人、最后告诉教会——为我们提供了清晰的路线图。</p>

<blockquote>「真正属灵的牧者不是逃避冲突的人，而是能在冲突中将人的心引向基督的人。」</blockquote>

<h2>五、冲突后的重建</h2>
<p>即使冲突解决了，关系也可能留下伤痕。这时需要牧者有意识地创造条件帮助双方重建信任——可能是共同服侍的机会、一起用餐，或是持续的跟进关怀。</p>`,
    category: '牧养实践',
    categorySlug: 'pastoral-practice',
    author: '张牧',
    date: '2026-06-28',
    imageUrl: 'https://readdy.ai/api/search-image?query=Two%20hands%20reaching%20toward%20each%20other%20in%20a%20gesture%20of%20reconciliation%2C%20warm%20soft%20lighting%2C%20olive%20branch%20in%20the%20background%2C%20peaceful%20atmosphere%2C%20symbolic%20of%20healing%20and%20unity%2C%20minimalist%20composition&width=800&height=450&seq=article-pastoral-01&orientation=landscape',
    tags: ['教会治理', '冲突化解', '牧养智慧'],
    readTime: '11分钟',
    featured: false,
  },
  {
    id: 5,
    title: '从诗篇学习祷告：用神的话语向神说话',
    slug: 'praying-with-psalms',
    excerpt: '诗篇是圣经中的祷告书。当我们不知道如何祷告时，诗篇为我们提供了最丰富、最真实的祷告语言。学习用诗篇祷告，让我们的灵修生活焕然一新。',
    content: `<h2>引言：当你不懂得如何祷告时</h2>
<p>你是否曾经坐在那里，想要祷告，却不知道该说什么？圣灵用说不出的叹息为我们代求（罗8:26），而神也在诗篇中为我们预备了一百五十篇受圣灵感动的祷文。</p>
<p>宗教改革家马丁·路德称诗篇为「小圣经」，加尔文则说诗篇是「灵魂各部分的解剖学」，因为在其中我们可以找到人类每一种情感的表达。</p>

<h2>一、诗篇教导我们全方位的情感表达</h2>
<p>现代教会有时将某些情绪视为「不属灵」——愤怒、怀疑、绝望、哀伤。但诗篇毫不避讳地表达了这一切。当你读到「我的神，我的神，为什么离弃我？」（诗22:1），你知道即使是最深的绝望，也可以带到神面前。</p>

<h2>二、诗篇重塑我们的焦点</h2>
<p>许多时候我们的祷告是以自我为中心的——我的需要、我的问题、我的感受。但诗篇不断地将我们的目光从自己转向神。即使是以哀叹开始的诗篇，常常以赞美结束（如诗篇13、42-43）。</p>

<h2>三、如何用诗篇祷告</h2>
<p>实用的方法包括：每天读一篇诗篇，然后用自己的话重述；将诗篇中的「我」换成自己，让经文成为个人的祷告；或者直接以诗篇为祷告词，用心默念。</p>

<blockquote>「诗篇是神赐给我们的话语，让我们用这些话语来回应他。这是最安全的祷告——因为你所祷告的，正是神所默示的。」</blockquote>`,
    category: '灵命成长',
    categorySlug: 'spiritual-growth',
    author: '王牧',
    date: '2026-06-20',
    imageUrl: 'https://readdy.ai/api/search-image?query=Open%20ancient%20Bible%20with%20soft%20candlelight%20on%20wooden%20table%2C%20prayer%20hands%20folded%20nearby%2C%20warm%20amber%20glow%2C%20peaceful%20morning%20devotion%20scene%2C%20reverent%20atmosphere%2C%20minimalist%20still%20life&width=800&height=450&seq=article-psalm-01&orientation=landscape',
    tags: ['祷告', '诗篇', '灵修'],
    readTime: '9分钟',
    featured: true,
  },
  {
    id: 6,
    title: '小组查经带领的十个实用技巧',
    slug: 'small-group-bible-study-tips',
    excerpt: '带领小组查经是一门需要操练的艺术。从预备到提问，从时间管理到处理沉默，这里有十个经得起实践检验的技巧。',
    content: `<h2>引言：查经带领者的挑战</h2>
<p>你可能刚刚被邀请带领一个小组查经，或者在教会中已经服侍了一段时间，但总觉得查经的氛围不够活跃、讨论不够深入。带领查经不是讲课，而是引导——引导组员自己去发现经文中的宝藏。</p>

<h2>技巧一：先自己浸泡在经文中</h2>
<p>不要依赖查经材料来替代你自己的研经。在参考任何注释之前，先把经文反复读几遍，用自己的话写出观察和问题。只有当你的心先被经文触动，你才能带领别人进入经文的世界。</p>

<h2>技巧二：设计好的问题</h2>
<p>好问题是查经的引擎。三类问题可以帮助你：观察性问题（「这段经文说了什么？」）、解释性问题（「这是什么意思？」）和应用性问题（「这对我们有什么意义？」）。</p>

<h2>技巧三：善用沉默</h2>
<p>新带领者最常犯的错误是害怕沉默。你问了一个问题，3秒钟没人回答，你就自己说出来了。给组员至少7-10秒的思考时间。沉默不是在浪费时——而是在给圣灵工作的空间。</p>

<h2>技巧四：把焦点从自己身上移开</h2>
<p>如果你的小组讨论总是由你一个人主导，那不是带领查经，而是讲课。学习用「你们怎么看？」「还有谁有不同角度的看见？」来激发讨论。</p>

<h2>技巧五：从经文到生活</h2>
<p>查经不能只停留在知识层面。每一段经文都应该导向一个可操作的应用。不是笼统的「我们要更爱神」，而是具体的「这周我可以做什么来回应这段经文？」</p>

<h2>技巧六至十</h2>
<p>其他关键技巧包括：控制时间节奏、处理「话多」和「话少」的组员、将讨论拉回正轨、营造安全分享的氛围，以及以祷告开始和结束每一次查经。</p>

<blockquote>「最好的查经带领者不是知识最渊博的人，而是最能帮助别人自己发现真理的人。」</blockquote>`,
    category: '门徒训练',
    categorySlug: 'discipleship-training',
    author: '李传道',
    date: '2026-06-15',
    imageUrl: 'https://readdy.ai/api/search-image?query=A%20small%20group%20of%20people%20sitting%20in%20a%20circle%20in%20a%20cozy%20living%20room%20with%20open%20Bibles%2C%20warm%20lamp%20light%2C%20engaged%20discussion%2C%20community%20atmosphere%2C%20soft%20natural%20colors%2C%20peaceful%20gathering&width=800&height=450&seq=article-bible-study-01&orientation=landscape',
    tags: ['查经', '小组带领', '门徒训练'],
    readTime: '10分钟',
    featured: false,
  },
  {
    id: 7,
    title: '教会年历与节期讲道的规划策略',
    slug: 'liturgical-calendar-preaching',
    excerpt: '按照教会年历安排讲道系列，可以帮助会众系统性地经历基督救恩的全貌。从将临期到圣灵降临期，本文提供全年讲道规划建议。',
    content: `<h2>引言：为什么需要教会年历？</h2>
<p>在现代快节奏的生活中，教会年历提供了一个反文化的节奏——一个以基督生平为中心的敬拜节奏。它帮助会众年复一年地经历等候、降生、显现、受难、复活和圣灵降临的完整救恩故事。</p>

<h2>将临期（圣诞节前四周）</h2>
<p>主题：等候与盼望。这是一个预备心灵的季节，教导会众在黑暗中等候真光的到来。适合宣讲旧约中的弥赛亚预言。</p>

<h2>圣诞期（12月25日至1月5日）</h2>
<p>主题：道成肉身。庆祝神成为人的奥秘和恩典。适合宣讲约翰福音第一章、腓立比书第二章等。</p>

<h2>显现期（1月6日至大斋前）</h2>
<p>主题：基督的彰显。基督向世界显现——东方博士、耶稣受洗、迦拿婚宴。可以安排耶稣早期事工的系列讲道。</p>

<h2>大斋期（复活节前四十天）</h2>
<p>主题：悔改与反思。这是教会最庄严的季节，帮助会众面对自己的罪和基督的牺牲。适合宣讲登山宝训或其他关于门徒代价的经文。</p>

<h2>复活期（复活节至五旬节）</h2>
<p>主题：新生命。五十天的庆祝！宣讲复活后的显现记载、新约书信中关于复活得胜的教导。</p>

<h2>圣灵降临期（五旬节后）</h2>
<p>主题：在圣灵中成长。一年中最长的季节，适合安排系统性的书卷研读或主题系列。</p>

<blockquote>「教会年历不是律法主义的捆绑，而是恩典的工具——帮助我们在时间的循环中，不断被基督的故事塑造。」</blockquote>`,
    category: '讲道资源',
    categorySlug: 'sermon-resources',
    author: '张牧',
    date: '2026-06-08',
    imageUrl: 'https://readdy.ai/api/search-image?query=A%20beautiful%20church%20liturgical%20calendar%20displayed%20on%20a%20wooden%20wall%20with%20seasonal%20colors%20of%20purple%20white%20green%20and%20red%2C%20candles%20nearby%2C%20warm%20sacred%20atmosphere%2C%20soft%20natural%20lighting%2C%20reverent%20composition&width=800&height=450&seq=article-liturgical-01&orientation=landscape',
    tags: ['讲道规划', '教会年历', '节期'],
    readTime: '14分钟',
    featured: false,
  },
  {
    id: 8,
    title: '牧者自我关顾：防止事奉枯竭的七个原则',
    slug: 'pastor-self-care',
    excerpt: '牧者是群羊的照顾者，但谁来照顾牧者？事奉中的枯竭现象越来越普遍。本文分享七个经得起考验的自我关顾原则。',
    content: `<h2>引言：一个被忽视的危机</h2>
<p>统计数据令人警醒：每年有大量牧者因身心耗竭而离开事奉岗位。他们忠心喂养群羊，却忘记了自己也是一只需要被牧养的羊。</p>
<p>耶稣在忙碌的事奉中，常常「退到旷野去祷告」（路5:16）。如果连神的儿子都需要定期从人群中抽离，我们岂不更需要吗？</p>

<h2>原则一：承认自己的有限</h2>
<p>你是有限的，只有神是无限的。接受这个事实不是缺乏信心，而是谦卑的表现。你不能满足所有人的期待，你也不可能一周七天二十四小时待命。</p>

<h2>原则二：守护与神独处的时间</h2>
<p>最容易在忙碌中牺牲的，恰恰是最重要的。当讲道预备变成纯粹的任务，当读经只是为了找讲道材料——危险信号已经亮起。你需要为自己——不是为会众——而与神独处的时间。</p>

<h2>原则三：建立健康的支持系统</h2>
<p>找到可以坦诚分享的同工或属灵同伴。不是下属，不是会众，而是可以安全地表达软弱、怀疑和挣扎的人。</p>

<h2>原则四：设立安息日</h2>
<p>事奉者往往是最不遵守安息日的人。真正的安息日不是「什么都不做」，而是「在神里面恢复」。选择一个固定的一天，关掉手机，与家人相处，做那些让你生命得到滋养的事。</p>

<h2>原则五至七</h2>
<p>还包括：保持身体健康（睡眠、运动、饮食）、继续学习和成长（读非事奉类的书、培养兴趣爱好）、以及定期进行属灵导引。</p>

<blockquote>「一个枯竭的牧者无法牧养一群饥渴的羊。自我关顾不是自私，而是对呼召的忠心。」</blockquote>`,
    category: '牧养实践',
    categorySlug: 'pastoral-practice',
    author: '陈弟兄',
    date: '2026-05-30',
    imageUrl: 'https://readdy.ai/api/search-image?query=A%20peaceful%20scene%20of%20a%20person%20sitting%20quietly%20by%20a%20lake%20at%20sunrise%2C%20journal%20and%20coffee%20nearby%2C%20mist%20over%20water%2C%20tranquil%20self-care%20moment%2C%20warm%20soft%20morning%20light%2C%20minimalist%20composition&width=800&height=450&seq=article-selfcare-01&orientation=landscape',
    tags: ['牧者关怀', '事奉健康', '安息'],
    readTime: '10分钟',
    featured: false,
  },
  {
    id: 9,
    title: '提摩太前后书的教牧领导学',
    slug: 'pastoral-leadership-timothy',
    excerpt: '保罗写给提摩太的两封书信是新约中最重要的教牧书信。从中我们可以学习关于领袖品格、教会治理和代际传承的重要原则。',
    content: `<h2>引言：一位年轻牧者的挑战</h2>
<p>提摩太是一位年轻的牧者，在以弗所这个充满挑战的城市带领教会。保罗在罗马监狱中写给他的两封信，不仅是给提摩太的，也是给每一个时代承担牧养责任的仆人的。</p>

<h2>一、品格先于恩赐</h2>
<p>在提摩太前书第3章，保罗列出长老和执事的资格，几乎全部与品格有关——只有「善于教导」一项涉及能力。保罗似乎在说：在神的家中，你是谁比你做什么更重要。</p>

<h2>二、纯正教义的重要性</h2>
<p>保罗反复强调「纯正的道理」（提前1:10；提后4:3）。在信息泛滥的时代，教会更需要领袖有能力辨别和持守纯正的教义。</p>

<h2>三、代际传承</h2>
<p>提摩太后书2:2描绘了一个四代的传承链：保罗→提摩太→忠心能教导的人→别人。每一位牧者都应当思考：「我正在培养谁来接续这神圣的托付？」</p>

<blockquote>「牧养不是建造自己的王国，而是为下一代预备忠心的工人。」</blockquote>`,
    category: '讲道资源',
    categorySlug: 'sermon-resources',
    author: '王牧',
    date: '2026-05-22',
    imageUrl: 'https://readdy.ai/api/search-image?query=Ancient%20scroll%20and%20quill%20on%20a%20weathered%20wooden%20desk%20with%20soft%20candlelight%2C%20sacred%20writing%20atmosphere%2C%20warm%20amber%20tones%2C%20historical%20biblical%20setting%2C%20reverent%20and%20scholarly%2C%20minimalist%20composition&width=800&height=450&seq=article-timothy-01&orientation=landscape',
    tags: ['教牧书信', '领袖品格', '提摩太'],
    readTime: '13分钟',
    featured: false,
  },
  {
    id: 10,
    title: '信仰与工作的整合：在职场上活出福音',
    slug: 'faith-work-integration',
    excerpt: '许多信徒将信仰与工作分割，但圣经的教导是整全的。如何在职场中见证基督，将日常工作视为神圣的呼召？',
    content: `<h2>引言：主日基督徒的困境</h2>
<p>你是否曾经觉得，周日你在教会敬拜服侍，但周一到周五你在办公室做的事情与信仰毫无关系？这种「分割」是现代基督徒最常见的挣扎之一。</p>
<p>然而，圣经的创造叙事告诉我们，工作早在堕落之前就已经存在。亚当在伊甸园中的「修理看守」（创2:15）就是工作——那时一切都是好的，工作是敬拜的一部分，而非诅咒。</p>

<h2>一、工作是呼召，不是惩罚</h2>
<p>宗教改革恢复了「信徒皆祭司」的真理，也恢复了「工作即呼召」的观念。马丁·路德说，一个挤奶的女仆若为荣耀神而工作，她的工作在神眼中与修士的祷告同样神圣。</p>

<h2>二、以卓越见证主</h2>
<p>但以理在巴比伦的职场中，「有美好的灵性」，并且「忠心办事，毫无错误过失」（但6:3-4）。他的卓越成为他见证的平台。基督徒应当是在职场中最可靠、最勤奋、最诚信的人。</p>

<h2>三、建立关系的桥梁</h2>
<p>职场是大多数信徒与非信徒建立深度关系的唯一场所。不要急于「传福音」，先「活出福音」。真实的关怀、正直的品格、在压力中的平安——这些都是最强有力的见证。</p>

<blockquote>「你工作的场所，就是你被差派的禾场。你的工位，就是你的讲台。」</blockquote>`,
    category: '门徒训练',
    categorySlug: 'discipleship-training',
    author: '李传道',
    date: '2026-05-15',
    imageUrl: 'https://readdy.ai/api/search-image?query=A%20professional%20workspace%20with%20an%20open%20Bible%20subtly%20placed%20on%20a%20modern%20desk%2C%20warm%20morning%20light%20through%20window%2C%20clean%20minimalist%20office%2C%20integration%20of%20faith%20and%20work%2C%20peaceful%20atmosphere&width=800&height=450&seq=article-work-01&orientation=landscape',
    tags: ['职场信仰', '工作呼召', '福音见证'],
    readTime: '9分钟',
    featured: false,
  },
  {
    id: 11,
    title: '青少年事工的突破：从「节目」到「关系」',
    slug: 'youth-ministry-breakthrough',
    excerpt: '许多教会青少年事工陷入「靠节目吸引人」的循环中。本文探讨如何从关系出发，建立真正能影响下一代生命的事工模式。',
    content: `<h2>引言：流失的一代</h2>
<p>数据显示，大量在教会中长大的年轻人在进入大学后离开了信仰。这个令人心痛的现象背后，可能隐藏着我们青少年事工模式的根本性问题。</p>

<h2>一、节目无法替代关系</h2>
<p>好的音乐、有趣的游戏、感人的见证——这些可以吸引青少年参加活动，但不能建立持久的信仰。研究表明，那些在信仰中稳固成长的年轻人，都有一个共同点：他们与至少一位（非父母的）属灵成年人有真实、深度的关系。</p>

<h2>二、陪伴胜于教导</h2>
<p>青少年需要的不仅仅是正确的教义（虽然这很重要），他们更需要看到信仰如何在真实生活中运作。这意味着陪伴——让他们看到你如何面对压力、如何处理失败、如何在平凡的日子中跟随基督。</p>

<blockquote>「年轻人不会因为你说得对而跟随你，他们会因为你真实而倾听你。」</blockquote>`,
    category: '牧养实践',
    categorySlug: 'pastoral-practice',
    author: '陈弟兄',
    date: '2026-05-08',
    imageUrl: 'https://readdy.ai/api/search-image?query=A%20group%20of%20young%20people%20sitting%20in%20a%20circle%20outdoors%20on%20grass%20having%20a%20meaningful%20conversation%2C%20warm%20golden%20hour%20light%2C%20genuine%20connection%2C%20peaceful%20community%20atmosphere%2C%20natural%20setting&width=800&height=450&seq=article-youth-01&orientation=landscape',
    tags: ['青少年事工', '下一代', '关系牧养'],
    readTime: '8分钟',
    featured: false,
  },
  {
    id: 12,
    title: '解经式讲道的预备流程：从经文到讲章',
    slug: 'expository-preaching-preparation',
    excerpt: '一篇解经式讲道的诞生，从选经文、做观察、查考原文、写大纲到实际宣讲，每一步都需要严谨和祷告。本文分享一套成熟的讲章预备流程。',
    content: `<h2>引言：预备讲章的属灵纪律</h2>
<p>每周按时站上讲台，是牧者最大的特权，也是最大的压力。一篇有深度的讲章不会凭空产生——它需要纪律、方法和圣灵的工作相结合。</p>

<h2>第一步：尽早选定经文</h2>
<p>建议在讲道日期前至少两周选定经文。这让经文有充分的时间「泡」在你的心里。利用零碎时间反复默想经文，让圣灵在日常生活中向你开启经文的意义。</p>

<h2>第二步：深入的观察</h2>
<p>在查考任何注释之前，先自己反复阅读经文（建议至少十遍）。用不同的译本，写下你的观察：重复的词汇、对比、因果关系、连接词、人物、地点……问自己：「这段经文在当时对最初的读者意味着什么？」</p>

<h2>第三步：研经工具的使用</h2>
<p>在完成自己的观察后，开始使用研经工具和注释书。建议先查原文工具（如Strong编码）、再读背景注释、最后读解经注释。这个顺序很重要：避免被别人的结论主导你的理解。</p>

<h2>第四步：确定中心思想</h2>
<p>将整段经文的主题归纳为一句话——这就是你的讲道核心。每一篇好的讲道都围绕一个清晰的中心思想展开。如果你不能用一句话说清楚这篇讲道要讲什么，你就还没预备好。</p>

<h2>第五步：搭建讲章架构</h2>
<p>以中心思想为核心，搭建讲章结构。引言帮助会众进入经文的世界；主体按照经文的自然分段展开；结论将经文应用到今天的生活，并指向福音。</p>

<blockquote>「好的讲道不是从讲员的创意中诞生的，而是从经文的深度中被挖掘出来的。」</blockquote>`,
    category: '讲道资源',
    categorySlug: 'sermon-resources',
    author: '张牧',
    date: '2026-04-28',
    imageUrl: 'https://readdy.ai/api/search-image?query=A%20pastor%20studying%20at%20a%20wooden%20desk%20with%20multiple%20open%20Bibles%20and%20commentaries%2C%20warm%20desk%20lamp%20light%2C%20notebook%20with%20handwritten%20notes%2C%20scholarly%20yet%20devotional%20atmosphere%2C%20soft%20warm%20tones&width=800&height=450&seq=article-expository-01&orientation=landscape',
    tags: ['解经讲道', '讲章预备', '研经方法'],
    readTime: '12分钟',
    featured: false,
  },
];